import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, X } from "lucide-react";
import { api } from "../lib/api";

function buildSteps(profiles, cals) {
  const withTasks = profiles.find((p) => (p.tasks || []).length > 0);
  const firstProfile = profiles[0];
  const openCal = cals.find((c) => !(c.employee_submitted && c.manager_submitted));
  const latest = openCal || cals[0];
  const empDone = cals.find((c) => c.employee_submitted);
  const mgrDone = cals.find((c) => c.manager_submitted);
  const bothDone = cals.find((c) => c.employee_submitted && c.manager_submitted);
  const toDiscuss = cals.find((c) => c.employee_submitted && c.manager_submitted && !c.conversation_held_on);
  const held = cals.find((c) => c.conversation_held_on);
  return [
    { title: "Create your first employee profile", hint: "Add the person you'll calibrate with.", done: profiles.length > 0, to: "/profiles/new", cta: "Create profile" },
    { title: "Add their Position Description", hint: "Upload or generate a PD — AI extracts measurable tasks & KPIs.", done: !!withTasks, to: firstProfile ? `/profiles/${firstProfile.id}` : "/profiles/new", cta: "Add PD" },
    { title: "Start a calibration", hint: "The employee gets a secret invite link by email — no account needed.", done: cals.length > 0, to: withTasks ? `/profiles/${withTasks.id}` : firstProfile ? `/profiles/${firstProfile.id}` : "/profiles/new", cta: "Start calibration" },
    { title: "Employee adds goals & self-rates", hint: "Waiting on them? Copy or resend the invite from the calibration page.", done: !!empDone, to: latest ? `/calibrations/${latest.id}` : null, cta: "Open calibration" },
    { title: "You rate — blind", hint: "Neither side sees the other's scores until both submit.", done: !!mgrDone, to: latest ? `/calibrations/${latest.id}/rate` : null, cta: "Rate now" },
    { title: "Review the comparison & hold the conversation", hint: "See the gaps and AI coaching side by side, talk it through, then mark the conversation as held.", done: !!held, to: toDiscuss ? `/calibrations/${toDiscuss.id}` : bothDone ? `/calibrations/${bothDone.id}` : null, cta: "Mark as held" },
  ];
}

function StepRow({ step, index, isNext, showCta }) {
  return (
    <li className={`flex flex-wrap sm:flex-nowrap items-start gap-3 py-3 border-b border-[#111]/10 last:border-0 ${step.done ? "opacity-60" : ""}`} data-testid={`onboarding-step-${index}`} data-state={step.done ? "done" : isNext ? "next" : "todo"}>
      <span className={`w-6 h-6 shrink-0 flex items-center justify-center font-mono-cal text-[11px] ${step.done ? "bg-[#00A36C] text-white" : isNext ? "bg-[#FF4F00] text-white" : "border border-[#111]/30"}`}>
        {step.done ? <Check size={13} /> : index + 1}
      </span>
      <div className="flex-1 min-w-[180px]">
        <div className={`font-display font-semibold text-sm ${step.done ? "line-through" : ""}`}>{step.title}</div>
        {!step.done && <div className="text-xs text-[#666] mt-0.5">{step.hint}</div>}
      </div>
      {showCta && (
        <Link to={step.to} className={`cal-btn !py-1.5 !px-3 text-xs whitespace-nowrap ml-9 sm:ml-0 ${isNext ? "" : "cal-btn-ghost"}`} data-testid={`onboarding-step-${index}-cta`}>
          {step.cta} <ArrowRight size={12} />
        </Link>
      )}
    </li>
  );
}

export const GettingStartedChecklist = ({ profiles, onDismiss }) => {
  const [cals, setCals] = useState(null);
  useEffect(() => {
    api.get("/calibrations").then((r) => setCals(r.data)).catch(() => setCals([]));
  }, [profiles]);
  if (!cals) return null;
  const steps = buildSteps(profiles, cals);
  const doneCount = steps.filter((s) => s.done).length;
  const nextIdx = steps.findIndex((s) => !s.done);
  const allDone = nextIdx === -1;
  return (
    <div className="cal-card cal-shadow p-5 md:p-6 mb-8 scroll-mt-20" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ GETTING STARTED ] {doneCount}/{steps.length}</div>
          <h2 className="font-display font-bold text-xl md:text-2xl mt-1">{allDone ? "You've completed your first calibration" : "From profile to calibration in 6 steps"}</h2>
        </div>
        <button onClick={onDismiss} className="w-10 h-10 shrink-0 flex items-center justify-center border border-[#111] hover:bg-[#111] hover:text-white transition-colors" aria-label="Hide guide" title="Hide guide" data-testid="onboarding-dismiss">
          <X size={14} />
        </button>
      </div>
      <div className="mt-3 h-1 bg-[#111]/10" aria-hidden="true">
        <div className="h-1 bg-[#FF4F00] transition-[width] duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} data-testid="onboarding-progress" />
      </div>
      <ol className="mt-2">
        {steps.map((s, i) => (
          <StepRow key={s.title} step={s} index={i} isNext={i === nextIdx} showCta={!s.done && !!s.to && (i === nextIdx || (i >= 3 && nextIdx >= 3))} />
        ))}
      </ol>
    </div>
  );
};

export default GettingStartedChecklist;
